import { embed } from "@/services/embeddings";
import { search as vectorSearch } from "@/services/vector";
import { logger } from "@/services/logger";
import type { KnowledgeScope } from "@/types";

const log = logger.scope("rag");
const MAX_CONTEXT_CHUNKS = 6;

export interface RagContext {
  contextText: string;
  sources: { documentId: string; documentName: string }[];
}

export interface RagSettings {
  ollamaHost: string;
  embeddingModelId: string | null;
}

/**
 * Embeds `query`, runs it against the vector store scoped per `scope`, and
 * assembles a context block for the active provider. Returns `null` when
 * there's nothing usable to inject (scope is "none", no embedding model
 * configured, or no matches) — callers treat that as "send the message
 * without knowledge context" rather than an error.
 *
 * Takes `settings` as a parameter rather than reading `settingsStore`
 * directly — same discipline as `services/ai/chat/chatService.ts`, which
 * never imports a store either; `useChat` (the one caller) reads
 * `settingsStore` and passes values in, keeping this service trivially
 * testable against a mocked provider with no Zustand in the picture.
 */
export async function buildRagContext(
  query: string,
  scope: KnowledgeScope,
  settings: RagSettings,
): Promise<RagContext | null> {
  if (scope.mode === "none") return null;

  if (!settings.embeddingModelId) {
    log.warn("Knowledge scope is active but no embedding model is configured; skipping context.");
    return null;
  }

  try {
    const queryEmbedding = await embed(settings.ollamaHost, settings.embeddingModelId, query);
    const results = await vectorSearch(queryEmbedding, scope, MAX_CONTEXT_CHUNKS);
    if (results.length === 0) return null;

    const contextText = results
      .map((r, i) => `[${i + 1}] (${r.documentName})\n${r.chunk.content}`)
      .join("\n\n");

    const sources = Array.from(
      new Map(results.map((r) => [r.documentId, { documentId: r.documentId, documentName: r.documentName }])).values(),
    );

    return { contextText, sources };
  } catch (err) {
    log.warn("Failed to build knowledge context; continuing without it.", err);
    return null;
  }
}

/** Wraps a context block in the instruction text sent to the model. */
export function formatContextMessage(context: RagContext): string {
  return `Use the following context from the user's Knowledge library to answer, if relevant. Cite sources by name when you use them.\n\n${context.contextText}`;
}
